import { useState, useEffect, useCallback } from 'react';
import { getReferees, getRefereesAllPages } from '@/api/TournamentManagement/refereeApi';
import type { RefereeListView, RefereeListQuery } from '@/models/tournament';

export function useReferees(query: RefereeListQuery) {
  const [data, setData] = useState<RefereeListView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getReferees(query);
      if (result.status < 400 && result.errors.length === 0) {
        setData(result.payload);
      } else {
        setError(result.errors[0]?.description || 'An error occurred');
      }
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [query.search, query.certification, query.page, query.pageSize]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
}

/**
 * Every referee in the company, not just the first server page.
 *
 * Use this for pickers, filter dropdowns and tables that render "all referees" — anything where the
 * user expects the whole roster. `useReferees` is server-paged and its `pageSize` is clamped to 100,
 * so passing a big number there silently drops everyone past the cap (see `getRefereesAllPages`).
 */
export function useAllReferees(query: Omit<RefereeListQuery, 'page' | 'pageSize'> = {}) {
  const [data, setData] = useState<RefereeListView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getRefereesAllPages(query);
      if (result.status < 400 && result.errors.length === 0) {
        setData(result.payload);
      } else {
        setError(result.errors[0]?.description || 'An error occurred');
      }
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.search, query.certification]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
}
